import { defineStore, acceptHMRUpdate } from 'pinia';
import { useUsersStore } from '@/stores/useUsers';
import { searchApi } from '@/services/searchService';

export const useSearchStore = defineStore('Search', {
  state: () => ({
    searchResults: [],
    searchModalControls: null,
    isSearchModalOpen: false, // New reactive state for modal visibility
    isSearching: false, // loading
    noResults: false,
    input: '',
    searchRecords: 0,
    hasMoreResults: false, // for pagination
    searchPage: 1,
    searchOffset: 0,
    searchLimit: 10,
    searchDirection: 'ASC',
    searchField: 'patient_name',
    searchOriginPath: '', // helps decide when to show modal when navigating
    hasLeftSearchOrigin: false, // if the user has already navigated away from searchOriginPath
    searchType: 'work_item', // default search type
  }),
  getters: {
    getSearchOriginPath: state => {
      return state.searchOriginPath;
    },
  },
  actions: {
    // Recording an origin (or clearing it with '') always resets the
    // click-through, so the two can never disagree
    setSearchOriginPath(path) {
      this.searchOriginPath = path;
      this.hasLeftSearchOrigin = false;
    },
    markLeftSearchOrigin() {
      this.hasLeftSearchOrigin = true;
    },
    setSearchType(type) {
      this.searchType = type;
      if (this.input && this.input.trim() !== '') {
        if (type !== 'work_item' && this.input.length < 3) {
          return;
        }
        this.queryBySearchTerm();
      }
    },
    setSearchField(field) {
      this.searchField = field;
    },
    showSearchModal() {
      this.isSearchModalOpen = true;
    },
    // Navigating away — keeps the query and results so Back can restore them
    hideSearchModal() {
      this.isSearchModalOpen = false;
    },
    // Dismissed by the user — the search is discarded
    close() {
      this.isSearchModalOpen = false;
      this.clearSearch();
    },
    clearSearch() {
      this.searchResults = [];
      this.input = '';
      this.searchPage = 1;
      this.searchDirection = 'ASC';
      this.searchField = 'patient_name';
      this.setSearchOriginPath('');
      this.noResults = false;
    },
    async paginate(page) {
      await this.queryBySearchTerm(
        this.input,
        page,
        this.searchDirection,
        this.searchField
      );
    },
    toggleSortDirection() {
      const newDirection = this.searchDirection === 'ASC' ? 'DESC' : 'ASC';
      this.sortResults(newDirection, this.searchField);
    },
    async sortResults(direction, field) {
      this.searchDirection = direction;
      this.searchField = field;
      this.searchPage = 1;
      await this.queryBySearchTerm(this.input, 1, direction, field);
    },
    async queryBySearchTerm(
      query = this.input,
      page = 1,
      direction = 'ASC',
      field = 'patient_name'
    ) {
      if (!query || query.trim() === '') {
        return;
      }
      this.searchPage = page;
      this.searchDirection = direction.toUpperCase();
      this.searchField = field;
      this.isSearching = true;
      direction = this.searchDirection;
      const limit = this.searchLimit;
      this.searchLimit = limit;
      const payload = {
        query,
        limit,
        page,
        direction,
        field,
      };

      const userStore = useUsersStore();

      try {
        let response;
        if (this.searchType === 'note') {
          response = await searchApi.searchWorkItemNotes(payload);
        } else if (this.searchType === 'my_note') {
          payload.auxo_user_id = userStore.activeUser.auxo_user_id;
          response = await searchApi.searchWorkItemNotes(payload);
        } else {
          response = await searchApi.searchWorkItems(payload);
        }
        this.searchResults = response?.data || [];
        this.searchRecords = response?.data.length || 0;
        this.hasMoreResults = response?.has_more || false;
        this.noResults = this.searchRecords === 0;
        return this.searchResults;
      } finally {
        this.isSearching = false;
      }
    },
  },
});

// Enable Hot Module Replacement (HMR)
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useSearchStore, import.meta.hot));
}
